import { useEffect, useState } from "react";
import { BadgeCheck, Building2, Package, Power, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";

const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}>{children}</button>
);

export default function StoresTab() {
    const [stores, setStores] = useState(null);
    const [busy, setBusy] = useState(null);
    const [storeFilter, setStoreFilter] = useState("all");
    const [storeSearch, setStoreSearch] = useState("");
    const [storeSort, setStoreSort] = useState("name");
    const [pinAction, setPinAction] = useState(null);

    const load = () => api.get("/admin/stores").then((r) => setStores(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const toggleStore = async (c, pin) => {
        if (c.active && pin === undefined) {
            setPinAction({ label: `"${c.name}" mağazasını pasifleştirme (kullanıcıları panele erişemeyecek)`, run: (p) => toggleStore(c, p) });
            return;
        }
        setBusy(c.company_id);
        try {
            await api.post(`/admin/stores/${c.company_id}/status`, { active: !c.active }, pin ? { headers: { "X-Critical-Pin": pin } } : {});
            toast.success(`${c.name} ${!c.active ? "aktifleştirildi" : "pasifleştirildi"}`);
            setPinAction(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const deleteStore = async (c, pin) => {
        if (pin === undefined) {
            setPinAction({ label: `"${c.name}" mağazasını kalıcı olarak silme (kullanıcıları ve sepetleri de silinecek)`, run: (p) => deleteStore(c, p) });
            return;
        }
        setBusy(c.company_id);
        try {
            await api.delete(`/admin/records/companies/${c.company_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success(`${c.name} kalıcı olarak silindi`);
            setPinAction(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(null);
        }
    };

    const filtered = (stores || []).filter((c) => {
        const q = storeSearch.toLowerCase();
        const matchesQ = !q || c.name.toLowerCase().includes(q) || (c.city || "").toLowerCase().includes(q);
        const matchesF = storeFilter === "all" ? true : storeFilter === "active" ? c.active : !c.active;
        return matchesQ && matchesF;
    }).sort((a, b) =>
        storeSort === "name" ? a.name.localeCompare(b.name, "tr")
        : storeSort === "spend" ? (b.total_spend || 0) - (a.total_spend || 0)
        : storeSort === "orders" ? (b.order_count || 0) - (a.order_count || 0)
        : new Date(b.last_order_at || 0) - new Date(a.last_order_at || 0)
    );

    return (
        <div data-testid="cari-stores-tab">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={storeSearch} onChange={(e) => setStoreSearch(e.target.value)} placeholder="Mağaza veya şehir ara..." className="h-9 w-64 pl-8 text-sm" data-testid="cari-store-search" />
                </div>
                <Chip active={storeFilter === "all"} onClick={() => setStoreFilter("all")} tid="cari-store-filter-all">Tümü</Chip>
                <Chip active={storeFilter === "active"} onClick={() => setStoreFilter("active")} tid="cari-store-filter-active">Aktif</Chip>
                <Chip active={storeFilter === "passive"} onClick={() => setStoreFilter("passive")} tid="cari-store-filter-passive">Pasif</Chip>
                <select value={storeSort} onChange={(e) => setStoreSort(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="cari-store-sort">
                    <option value="name">Ad (A-Z)</option>
                    <option value="spend">Toplam Alım (çoktan aza)</option>
                    <option value="orders">Sipariş Sayısı</option>
                    <option value="recent">Son Sipariş (yeniden eskiye)</option>
                </select>
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} kayıt</span>
            </div>
            {!stores ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                        <table className="w-full text-sm" data-testid="cari-stores-table">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Mağaza / İşletme</th><th className="p-4">Kullanıcılar</th><th className="p-4">Sipariş</th><th className="p-4">Toplam Alım</th><th className="p-4">Son Sipariş</th><th className="p-4">Durum</th><th className="p-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.company_id} className={`border-b last:border-0 hover:bg-muted/40 ${!c.active ? "opacity-55" : ""}`} data-testid={`cari-store-row-${c.company_id}`}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600/15"><Building2 className="h-4 w-4 text-emerald-700" /></div>
                                                <div>
                                                    <p className="font-semibold">{c.name}</p>
                                                    <p className="text-xs text-muted-foreground">{c.city || "—"} {c.verified && <BadgeCheck className="ml-1 inline h-3.5 w-3.5 text-blue-600" />}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {c.users.map((u) => <p key={u.user_id} className="text-xs text-muted-foreground"><Users className="mr-1 inline h-3 w-3" />{u.name} <span className="opacity-70">({u.email})</span></p>)}
                                            {!c.users.length && <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                        <td className="p-4">{c.order_count}{c.open_orders > 0 && <Badge variant="outline" className="ml-1.5 border-amber-200 bg-amber-100 text-[10px] text-amber-800">{c.open_orders} açık</Badge>}</td>
                                        <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(c.total_spend)}</td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(c.last_order_at)}</td>
                                        <td className="p-4"><Badge variant="outline" className={`text-[10px] ${c.active ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`} data-testid={`cari-store-active-${c.company_id}`}>{c.active ? "Aktif" : "Pasif"}</Badge></td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-1.5">
                                                <Button size="sm" variant="outline" className={c.active ? "text-destructive" : "text-emerald-700"} disabled={busy === c.company_id} onClick={() => toggleStore(c)} data-testid={`cari-store-toggle-${c.company_id}`}>
                                                    <Power className="mr-1.5 h-3.5 w-3.5" /> {c.active ? "Pasifleştir" : "Aktifleştir"}
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-destructive" disabled={busy === c.company_id} onClick={() => deleteStore(c)} data-testid={`cari-store-delete-${c.company_id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!stores.length && <tr><td colSpan={7} className="p-16 text-center text-sm text-muted-foreground"><Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Kayıtlı mağaza yok.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <CriticalPinDialog open={!!pinAction} onClose={() => setPinAction(null)} onSubmit={(pin) => pinAction?.run(pin)} actionLabel={pinAction?.label || ""} busy={!!busy} />
        </div>
    );
}
